import React from 'react'
import {
	Button,
	Form,
	InputNumber,
	Select,
	Table,
	Tooltip,
	Typography,
} from 'antd'
import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons'
import { AllowedModule, allowedModuleTypes } from 'types'
import toTileCase from 'utils/toTitleCase'

const { Item, List } = Form

interface PropTypes {
	allowedModules?: AllowedModule[]
}

const getExtraField = (moduleType?: string) => {
	if (moduleType === 'BOT' || moduleType === 'FORM') {
		return {
			name: 'childCount' as const,
			label: 'Child limit',
			tooltip: 'Child limit (0 = Unlimited)',
		}
	}
	if (moduleType === 'SIGNALS') {
		return {
			name: 'childCount' as const,
			label: 'Mobile clients',
			tooltip: 'Max mobile clients (0 = None)',
		}
	}
	if (moduleType === 'BROADCAST') {
		return {
			name: 'totalContact' as const,
			label: 'Contact limit',
			tooltip: 'Contact limit (0 = Unlimited)',
		}
	}
	return null
}

const getModuleHint = (moduleType?: string) => {
	if (moduleType === 'DRIP') {
		return 'Limit = max Drip campaigns. Assign Drip permissions per role in the client CRM (Settings → Roles).'
	}
	if (moduleType === 'NOTIFIER') {
		return 'Limit = max Notifier lists. Assign Notifier permissions per role in the client CRM (Settings → Roles).'
	}
	return null
}

/**
 * Compact table editor for plan allowedModules (Form.List name="allowedModules").
 */
const AllowedModulesTable: React.FC<PropTypes> = ({ allowedModules }) => (
	<List name='allowedModules'>
		{(fields, { add, remove }) => (
			<>
				<Table
					size='small'
					pagination={false}
					rowKey='key'
					dataSource={fields}
					locale={{ emptyText: 'No modules added' }}
					columns={[
						{
							title: 'Module',
							key: 'type',
							width: '34%',
							render: (_, field) => (
								<Item
									name={[field.name, 'type']}
									rules={[{ required: true, message: 'Required' }]}
									style={{ marginBottom: 0 }}
								>
									<Select
										showSearch
										optionFilterProp='label'
										placeholder='Select module'
										options={allowedModuleTypes.map(type => ({
											label: toTileCase(type),
											value: type,
											disabled:
												allowedModules?.some(
													(module, index) => module.type === type && index !== field.name
												) ?? false,
										}))}
									/>
								</Item>
							),
						},
						{
							title: (
								<Tooltip title='0 = Unlimited'>
									<span>Limit</span>
								</Tooltip>
							),
							key: 'count',
							width: '18%',
							render: (_, field) => (
								<Item
									name={[field.name, 'count']}
									rules={[{ required: true, message: 'Required' }]}
									style={{ marginBottom: 0 }}
								>
									<InputNumber min={0} style={{ width: '100%' }} />
								</Item>
							),
						},
						{
							title: 'Extra',
							key: 'extra',
							width: '34%',
							render: (_, field) => {
								const moduleType = allowedModules?.[field.name]?.type
								const extra = getExtraField(moduleType)
								const hint = getModuleHint(moduleType)

								if (!extra && !hint) {
									return (
										<Typography.Text type='secondary' style={{ fontSize: 12 }}>
											—
										</Typography.Text>
									)
								}

								return (
									<>
										{extra && (
											<Item
												name={[field.name, extra.name]}
												rules={[{ required: true, message: 'Required' }]}
												style={{ marginBottom: 0 }}
												tooltip={extra.tooltip}
											>
												<InputNumber
													min={0}
													placeholder={extra.label}
													style={{ width: '100%' }}
												/>
											</Item>
										)}
										{hint && (
											<Typography.Text
												type='secondary'
												style={{ fontSize: 11, display: 'block', marginTop: 2 }}
											>
												{hint}
											</Typography.Text>
										)}
									</>
								)
							},
						},
						{
							title: '',
							key: 'actions',
							width: 48,
							align: 'center' as const,
							render: (_, field) => (
								<Button
									htmlType='button'
									type='text'
									danger
									icon={<MinusCircleOutlined />}
									onClick={() => remove(field.name)}
									aria-label='Remove module'
								/>
							),
						},
					]}
				/>
				<Button
					htmlType='button'
					type='dashed'
					icon={<PlusOutlined />}
					onClick={() =>
						add({
							type: null,
							count: 0,
						})
					}
					style={{ marginTop: 8 }}
					block
				>
					Add Module
				</Button>
			</>
		)}
	</List>
)

AllowedModulesTable.defaultProps = {
	allowedModules: undefined,
}

export default AllowedModulesTable
